/**
 * useAuth Hook
 * 
 * Authentication hook providing auth state and methods.
 * Used by both admin-console and agent-ui.
 */

import { createContext, useContext, ReactNode, ReactElement, useEffect, useState, useCallback, useRef } from 'react';
import { 
  AuthState, 
  AuthContextValue, 
  AuthenticatorAssuranceLevel,
  User,
  identityToUser,
  hasMfaConfigured as checkMfaConfigured,
  getAal,
} from '../types/auth';
import { getIdentityClient, getIdentityBrowserUrl } from '../lib/identity-client';

/** Session refresh interval in milliseconds (5 minutes) */
const SESSION_REFRESH_INTERVAL = 5 * 60 * 1000;

/** Storage key for cross-tab session sync */
const SESSION_SYNC_KEY = 'atlas_session_sync';

// =============================================================================
// Auth Context
// =============================================================================

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
  /** Redirect to login if not authenticated */
  requireAuth?: boolean;
  /** URL to redirect to after login */
  returnTo?: string;
}

export function AuthProvider({ 
  children, 
  requireAuth = false,
  returnTo,
}: AuthProviderProps): ReactElement {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    session: null,
    error: null,
    aal: null,
    hasMfaConfigured: false,
  });

  const refreshIntervalRef = useRef<number | null>(null);
  const isInitialCheckRef = useRef(true);
  const browserUrl = getIdentityBrowserUrl();

  /**
   * Check for existing session.
   * On initial mount, sets isLoading=true to show loading UI.
   * On periodic/background refreshes, updates state silently to avoid
   * unmounting the component tree (which destroys active conversations).
   */
  const checkSession = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;

    try {
      if (!silent) {
        setState(prev => ({ ...prev, isLoading: true, error: null }));
      }
      
      const identityClient = getIdentityClient();
      const { data: session } = await identityClient.toSession();
      
      if (session && session.identity) {
        const user = identityToUser(session.identity);
        const aal = getAal(session);
        const mfaConfigured = checkMfaConfigured(session);
        
        // Store session for cross-tab sync
        sessionStorage.setItem('ory_session', JSON.stringify({
          session_id: session.id,
          identity_id: session.identity.id,
          aal,
        }));

        // Broadcast session update to other tabs
        localStorage.setItem(SESSION_SYNC_KEY, JSON.stringify({
          event: 'session_updated',
          timestamp: Date.now(),
        }));
        
        setState({
          isAuthenticated: true,
          isLoading: false,
          user,
          session,
          error: null,
          aal,
          hasMfaConfigured: mfaConfigured,
        });
      } else {
        throw new Error('No active session');
      }
    } catch (error) {
      // Clear any stale session data
      sessionStorage.removeItem('ory_session');
      
      // Broadcast logout to other tabs
      localStorage.setItem(SESSION_SYNC_KEY, JSON.stringify({
        event: 'session_invalid',
        timestamp: Date.now(),
      }));
      
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        session: null,
        error: error instanceof Error ? error : new Error('Session check failed'),
        aal: null,
        hasMfaConfigured: false,
      });
      
      // Redirect to login if auth is required.
      // Send browser directly to AR login page (not Kratos self-service)
      // so we can pass return_to without Kratos adding aal=aal2 and erroring.
      if (requireAuth) {
        const redirectUrl = returnTo || window.location.href;
        window.location.href = `${browserUrl}/auth/login?return_to=${encodeURIComponent(redirectUrl)}`;
      }
    }
  }, [requireAuth, returnTo, browserUrl]);

  /**
    * Redirect to login flow
    */
  const login = useCallback((returnToUrl?: string) => {
    const redirectUrl = returnToUrl || window.location.href;
    window.location.href = `${browserUrl}/auth/login?return_to=${encodeURIComponent(redirectUrl)}`;
  }, [browserUrl]);

  /**
   * Redirect to registration flow
   */
  const register = useCallback((returnToUrl?: string) => {
    const redirectUrl = returnToUrl || window.location.href;
    window.location.href = `${browserUrl}/self-service/registration/browser?return_to=${encodeURIComponent(redirectUrl)}`;
  }, [browserUrl]);

  /**
   * Perform logout
   */
  const logout = useCallback(async () => {
    try {
      const identityClient = getIdentityClient();
      const { data: logoutFlow } = await identityClient.createBrowserLogoutFlow();

      // ISS-283: prefer the token-based logout (XHR POST — Kratos purges the
      // session and clears the cookie in the response) over navigating to
      // logout_url. The cross-origin top-level navigation to the AR logout
      // URL gets aborted by Firefox (499 / "context canceled" in Kratos,
      // session stays active). Fall back to logout_url only without a token.
      if (logoutFlow.logout_token) {
        await identityClient.updateLogoutFlow({ token: logoutFlow.logout_token });
      } else if (logoutFlow.logout_url) {
        window.location.href = logoutFlow.logout_url;
        return;
      }

      // Clear local session
      sessionStorage.removeItem('ory_session');

      // Broadcast logout to other tabs
      localStorage.setItem(SESSION_SYNC_KEY, JSON.stringify({
        event: 'logout',
        timestamp: Date.now(),
      }));

      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        session: null,
        error: null,
        aal: null,
        hasMfaConfigured: false,
      });

      // Same-origin redirect (reliable across browsers)
      window.location.href = '/auth/login';
    } catch (error) {
      console.error('Logout failed:', error);
      
      // Force clear session and reload
      sessionStorage.removeItem('ory_session');
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        session: null,
        error: null,
        aal: null,
        hasMfaConfigured: false,
      });
    }
  }, []);

  /**
   * Refresh session
   */
  const refresh = useCallback(async () => {
    await checkSession();
  }, [checkSession]);

  /**
   * Check if current session meets required AAL
   */
  const hasRequiredAal = useCallback((required: AuthenticatorAssuranceLevel): boolean => {
    if (!state.aal) return false;
    
    const aalLevels: AuthenticatorAssuranceLevel[] = ['aal0', 'aal1', 'aal2', 'aal3'];
    const currentLevel = aalLevels.indexOf(state.aal);
    const requiredLevel = aalLevels.indexOf(required);
    
    return currentLevel >= requiredLevel;
  }, [state.aal]);

  // Check session on mount
  useEffect(() => {
    checkSession();
    // After the initial mount check, all subsequent checks are silent
    isInitialCheckRef.current = false;
  }, [checkSession]);

  // Set up periodic session refresh (silent — no UI disruption)
  useEffect(() => {
    if (state.isAuthenticated) {
      refreshIntervalRef.current = window.setInterval(() => {
        checkSession({ silent: true });
      }, SESSION_REFRESH_INTERVAL);
    }
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [state.isAuthenticated, checkSession]);

  // Listen for cross-tab session sync events
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== SESSION_SYNC_KEY || !event.newValue) return;
      
      try {
        const data = JSON.parse(event.newValue);
        
        if (data.event === 'logout' || data.event === 'session_invalid') {
          // Another tab logged out or session became invalid
          sessionStorage.removeItem('ory_session');
          setState({
            isAuthenticated: false,
            isLoading: false,
            user: null,
            session: null,
            error: null,
            aal: null,
            hasMfaConfigured: false,
          });
          
          // Redirect to login
          if (requireAuth) {
            window.location.href = '/auth/login';
          }
        } else if (data.event === 'session_updated') {
          // Another tab refreshed the session, refresh ours too (silently)
          checkSession({ silent: true });
        }
      } catch {
        // Ignore parse errors
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [requireAuth, checkSession]);

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    register,
    refresh,
    hasRequiredAal,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Use authentication context
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Use current user (throws if not authenticated)
 */
export function useUser(): User {
  const { user, isAuthenticated } = useAuth();
  if (!isAuthenticated || !user) {
    throw new Error('User not authenticated');
  }
  return user;
}

/**
 * Check if user has a specific role
 */
export function useHasRole(role: string): boolean {
  const { user, isAuthenticated } = useAuth();
  if (!isAuthenticated || !user) {
    return false;
  }
  return user.roles.includes(role) || user.roles.includes('admin');
}

/**
 * Check if user has any of the specified roles
 */
export function useHasAnyRole(roles: string[]): boolean {
  const { user, isAuthenticated } = useAuth();
  if (!isAuthenticated || !user) {
    return false;
  }
  return roles.some(role => user.roles.includes(role)) || user.roles.includes('admin');
}
